import { PatientCommentDto } from '@lmt/shared-types';
import {
  IconDropDownButton,
  OptionProps,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/IconDropDownButton';
import {
  BadgeColor,
  BadgeSize,
  UserBadge,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/UserBadge';
import { getFormattedLongDateTime } from '@lmt/unicorn-shared/app/components/patients/common';
import React, { useState } from 'react';
import { EditCommentBlock } from './EditComment';

import styles from './UserComments.module.scss';
import OptionIcon from '@lmt/unicorn-shared/app/icons/option.svg?react';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { TagsViewer } from '@lmt/unicorn-shared/app/components/common/ui-kit/tags/TagsViewer';

export interface UserCommentProps {
  comment: PatientCommentDto;
  isCurrentUser: boolean;
  editComment: (id: number, comment: string) => void;
  deleteComment: (id: number) => void;
  isLast: boolean;
}

export const UserComment: React.FC<UserCommentProps> = (props) => {
  const { comment, isCurrentUser, editComment, deleteComment, isLast } = props;
  const i18n = useScopedTranslation('patients.overview.comments');
  const [isEdited, setEdited] = useState<boolean>(false);
  const [isExpanded, setExpanded] = useState<boolean>(false);
  const userName = `${comment.userFirstName} ${comment.userLastName}`;
  const options: OptionProps[] = [
    {
      displayValue: i18n.t('edit_button'),
      value: 'edit',
      onClick: () => setEdited(true),
    },
    {
      displayValue: i18n.t('delete_button'),
      value: 'delete',
      onClick: () => deleteComment(comment.id),
      color: ButtonColor.Danger,
    },
  ];
  return (
    <div
      data-last={isLast}
      className={styles.commentContainer}
      data-test-id={'user-comment-item'}
    >
      <div className={styles.commentHeader}>
        <div className={styles.commentTitle}>
          <UserBadge
            name={userName}
            color={BadgeColor.Primary}
            size={BadgeSize.Small}
          />
          <div className={styles.commentUserName} data-test-id={'username'}>
            {userName}
          </div>
          <div className={styles.commentDate} data-test-id={'date'}>
            {getFormattedLongDateTime(comment.createdAt)}
          </div>
        </div>

        <div className={styles.commentOptions}>
          <div
            className={styles.commentEdited}
            data-test-id="edited-comment-badge"
          >
            {comment.isEdited ? i18n.t('edited_badge') : ''}
          </div>
          {isCurrentUser && (
            <div>
              <IconDropDownButton
                color="grey"
                iconComponent={<OptionIcon className={styles.addContentIcon} />}
                options={options}
                optionListClassName={styles.addContentOptionListContainer}
              />
            </div>
          )}
        </div>
      </div>
      <div className={styles.commentData}>
        {isEdited ? (
          <EditCommentBlock
            close={() => setEdited(false)}
            comment={comment.comment}
            commentId={comment.id}
            editComment={editComment}
          />
        ) : (
          <div>
            {isExpanded || comment.comment.length < 180 ? (
              <div>
                <TagsViewer
                  text={comment.comment}
                  className={styles.commentText}
                  testId={'text'}
                />
                {comment.comment.length > 180 && (
                  <UniLink
                    className={styles.expandedLink}
                    color={ButtonColor.Primary}
                    title={i18n.t('show_less_button')}
                    onClick={() => setExpanded(false)}
                  />
                )}
              </div>
            ) : (
              <div>
                <div className={styles.shortenedHeightComment}>
                  <TagsViewer
                    text={comment.comment}
                    className={styles.commentText}
                    testId={'text'}
                  />
                </div>
                {comment.comment.length > 180 && (
                  <UniLink
                    testId={'see-all'}
                    className={styles.expandedLink}
                    color={ButtonColor.Primary}
                    title={i18n.t('see_all_button')}
                    onClick={() => setExpanded(true)}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
